import { useMemo } from "react";
import { useWatch } from "react-hook-form";
import { useIntl } from "react-intl";

import testIds from "constants/testIds";
import type { PackingListRow } from "@RequestFormV2/sections/PackingList/types";
import { canDeletePackingListRow } from "@RequestFormV2/sections/PackingList/utils/packingListRowActions";
import { usePackingList } from "@RequestFormV2/sections/PackingList/context";
import AddRowButton from "@RequestFormV2/sections/PackingList/components/shared/AddRowButton";
import PackingListTable from "@RequestFormV2/sections/PackingList/components/shared/PackingListTable";
import ContainerTableRow from "@RequestFormV2/sections/PackingList/components/ContainersSection/ContainerTableRow";
import { CONTAINER_COLUMN_WIDTHS } from "@RequestFormV2/sections/PackingList/components/shared/packingListColumnWidths";
import { useStopSelectOptions } from "@RequestFormV2/sections/PackingList/utils/stopSelectOptions";
import PackingListWorkflowTableLoading from "@RequestFormV2/sections/PackingList/components/shared/PackingListWorkflowTableLoading";
import {
  getPackingListWorkflowColumnFlags,
  isPackingListWorkflowConfigReady,
  isPackingListWorkflowSettingEnabled,
} from "@RequestFormV2/sections/PackingList/utils/packingListWorkflowFlags";

export default function ContainersSection() {
  const intl = useIntl();
  const packingList = useWatch({ name: "packing_list" }) as PackingListRow[] | undefined;
  const {
    location,
    packingListConfig,
    isLoadingPackingListConfig,
    appendContainer,
    containerRows,
    handlingUnitHashMap,
  } = usePackingList();
  const { isMultiStop } = useStopSelectOptions();

  const isWorkflowConfigReady = isPackingListWorkflowConfigReady(location, isLoadingPackingListConfig);
  const { hazardousEnabled, temperatureEnabled, dangerousEnabled } = getPackingListWorkflowColumnFlags(packingListConfig);
  const additionalColumnEnabled = useMemo(
    () => isPackingListWorkflowSettingEnabled(handlingUnitHashMap.container?.additional_field),
    [handlingUnitHashMap],
  );
  const additionalColumnHeader = useMemo(() => {
    const customLabel = handlingUnitHashMap.container?.additional_field_label;
    return (
      customLabel ||
      intl.formatMessage({
        id: "request-form.packing-list.additionalInformation",
        defaultMessage: "Additional information",
      })
    );
  }, [handlingUnitHashMap, intl]);

  const columns = useMemo(
    () => [
      {
        key: "container_type",
        header: intl.formatMessage({ id: "request-form.packing-list.containerType", defaultMessage: "Container type" }),
        width: CONTAINER_COLUMN_WIDTHS.containerType,
      },
      {
        key: "weight",
        header: intl.formatMessage({ id: "request-form.packing-list.weight", defaultMessage: "Weight" }),
        width: CONTAINER_COLUMN_WIDTHS.weight + CONTAINER_COLUMN_WIDTHS.weightUnit,
      },
      {
        key: "qty",
        header: intl.formatMessage({ id: "request-form.packing-list.qty", defaultMessage: "Qty" }),
        width: CONTAINER_COLUMN_WIDTHS.qty,
      },
      ...(isMultiStop
        ? [
            {
              key: "pickup_stop",
              header: intl.formatMessage({ id: "general.pickup", defaultMessage: "Pickup" }),
              width: CONTAINER_COLUMN_WIDTHS.stop,
            },
            {
              key: "delivery_stop",
              header: intl.formatMessage({ id: "general.delivery", defaultMessage: "Delivery" }),
              width: CONTAINER_COLUMN_WIDTHS.stop,
            },
          ]
        : []),
      ...(hazardousEnabled
        ? [
            {
              key: "hazardous",
              header: intl.formatMessage({ id: "request-form.packing-list.hazardous", defaultMessage: "Hazardous" }),
              width: CONTAINER_COLUMN_WIDTHS.toggle,
              className: "rfv2-packing-list-table__header-cell--center",
            },
          ]
        : []),
      ...(temperatureEnabled
        ? [
            {
              key: "temperature",
              header: intl.formatMessage({
                id: "request-form.packing-list.temperatureControl",
                defaultMessage: "Temperature control",
              }),
              width: CONTAINER_COLUMN_WIDTHS.temperature,
            },
          ]
        : []),
      ...(dangerousEnabled
        ? [
            {
              key: "dangerous",
              header: intl.formatMessage({
                id: "request-form.packing-list.dangerousGoods",
                defaultMessage: "Dangerous goods",
              }),
              width: CONTAINER_COLUMN_WIDTHS.dangerous,
            },
          ]
        : []),
      ...(additionalColumnEnabled
        ? [
            {
              key: "additional",
              header: additionalColumnHeader,
              width: CONTAINER_COLUMN_WIDTHS.additional,
            },
          ]
        : []),
    ],
    [
      intl,
      isMultiStop,
      hazardousEnabled,
      temperatureEnabled,
      dangerousEnabled,
      additionalColumnEnabled,
      additionalColumnHeader,
    ],
  );

  return (
    <section className="rfv2-packing-list__subsection">
      <div className="rfv2-packing-list__subsection-title">
        {intl.formatMessage({
          id: "request-form.packing-list.containers",
          defaultMessage: "Containers information",
        })}
      </div>

      {containerRows.length > 0 && !isWorkflowConfigReady && <PackingListWorkflowTableLoading />}

      {containerRows.length > 0 && isWorkflowConfigReady && (
        <PackingListTable columns={columns} scrollable testId={testIds.REQUEST_FORM_PACKING_LIST_CONTAINER_TABLE}>
          {containerRows.map(({ index, fieldId, row }, displayIndex) => (
            <ContainerTableRow
              key={fieldId}
              index={index}
              fieldId={fieldId}
              rowNumber={displayIndex + 1}
              row={row}
              deleteDisabled={!canDeletePackingListRow(packingList ?? [], index)}
              isMultiStop={isMultiStop}
            />
          ))}
        </PackingListTable>
      )}

      <AddRowButton
        label={intl.formatMessage({
          id: "request-form.packing-list.addContainer",
          defaultMessage: "Add container",
        })}
        onClick={appendContainer}
        testId={testIds.REQUEST_FORM_PACKING_LIST_ADD_CONTAINER_BUTTON}
      />
    </section>
  );
}
